import { Entity } from '@betgames/bg-state-manager';
import { GameId } from '@bg-shared/enums/GameId';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { MAX_LAST_RESULT } from '@bg-shared/constants/constants';
import { webSocketsRepository } from '@bg-shared/infrastructure/WebSockets';
import { lastResultsApi } from './LastResults.api';
import { ILastResultModel, ILastResultsResponse } from './interfaces';

export default class LastResultsEntity extends Entity<ILastResultModel[], GameId> {
    private timeoutMap: Map<GameId, number> = new Map();

    constructor() {
        super({
            key: 'LastResults',
            default: [],
        });
    }

    private clearGameTimeout(gameId: GameId): void {
        const timeoutId = this.timeoutMap.get(gameId);

        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeoutMap.delete(gameId);
        }
    }

    public async initGameResults(gameId: GameId): Promise<void> {
        try {
            const data = await lastResultsApi.getLastResults(gameId);

            const result: ILastResultsResponse[] = Array.isArray(data) ? data : data.items;

            if (!result) {
                console.warn(`No last results received for game ${gameId}`);
                return;
            }

            const formatted: ILastResultModel[] = result.map((item) => ({
                time: item.time,
                runId: item.runId,
                items: item.results,
            }));

            this.store(gameId).update(formatted);
        } catch (error) {
            console.error(`Failed to fetch last results for game ${gameId}:`, error);
        }
    }

    public subscribeForUpdates(gameId: GameId, withoutTimeout = false): () => void {
        const delay = partnerSettingsStore.getGameDelay(gameId);

        const handleEvent = (data: ILastResultsResponse): void => {
            const maxResults = MAX_LAST_RESULT[gameId] ?? 10;
            const currentResults = this.store(gameId).value;
            const results = currentResults.slice(0, maxResults - 1);

            const newResult: ILastResultModel = {
                runId: data.runId,
                items: data.results,
                time: data.time,
            };

            this.store(gameId).update([newResult, ...results]);
        };

        const getSubscription = (): (() => void) => {
            return lastResultsApi.subscribeLastResult(gameId, (data) => {
                if (withoutTimeout) {
                    handleEvent(data);
                } else {
                    this.clearGameTimeout(gameId);

                    const timeoutId = window.setTimeout(() => {
                        handleEvent(data);
                    }, delay);

                    this.timeoutMap.set(gameId, timeoutId);
                }
            });
        };

        let unsubscribe = getSubscription();

        const removeOnReconnect = webSocketsRepository.onReconnect(() => {
            this.clearGameTimeout(gameId);
            unsubscribe();

            this.initGameResults(gameId).then(() => {
                unsubscribe = getSubscription();
            });
        });

        return () => {
            this.clearGameTimeout(gameId);
            removeOnReconnect();
            unsubscribe();
            this.store(gameId).reset();
        };
    }
}

export const lastResultsEntity = new LastResultsEntity();
